import {
  createZone as apiCreateZone,
  createRoom as apiCreateRoom,
  createLoad as apiCreateLoad,
  createRCU,
  createScene as apiCreateScene,
  getErrorMessage
} from './api';
import { fetchZones } from '$lib/api/zone';
import { fetchRooms } from '$lib/api/room';
import { fetchInputDevices } from '$lib/api/input_device';
import { assignUnit, fetchRCUs } from '$lib/api/rcu';
import { fetchUnitById } from '$lib/queries/cache';
import { getQueryClient } from '$lib/state/queryClient';
import { router } from '$lib/router/router.svelte';
import { showSuccess, showError } from '$lib/state/toast.svelte';
import {
  invalidateAfterZoneCreate,
  invalidateAfterRoomCreate,
  invalidateAfterLoadCreate,
  invalidateAfterRcuUpdate,
  invalidateAfterSceneCreate
} from '$lib/state/invalidation';
import { selectEntity } from '$lib/router';

function unitIdFromRoute(): number {
  return router.unitId ?? 0;
}

async function requireUnit() {
  const unitId = unitIdFromRoute();
  if (!unitId) return null;
  return fetchUnitById(unitId);
}

async function fetchUnitZones(unitId: number) {
  const qc = getQueryClient();
  return qc.fetchQuery({
    queryKey: ['zones', { unitId }],
    queryFn: async () => {
      const all = await fetchZones();
      return all.filter((z) => z.unit === unitId);
    }
  });
}

async function fetchUnitRooms(unitId: number) {
  const zones = await fetchUnitZones(unitId);
  const zoneIds = new Set(zones.map((z) => z.id));
  const qc = getQueryClient();
  return qc.fetchQuery({
    queryKey: ['rooms', { unitId }],
    queryFn: async () => {
      const all = await fetchRooms();
      return all.filter((r) => zoneIds.has(r.zone));
    }
  });
}

async function fetchUnitInputDevices(unitId: number) {
  const rooms = await fetchUnitRooms(unitId);
  const roomIds = new Set(rooms.map((r) => r.id));
  const all = await fetchInputDevices(unitId);
  return all.filter((d) => d.location_room != null && roomIds.has(d.location_room));
}

export async function promptName(label: string, defaultValue = ''): Promise<string | null> {
  const value = window.prompt(label, defaultValue);
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export async function createZone() {
  const unit = await requireUnit();
  const name = await promptName('Zone name');
  if (!name || !unit) return null;

  try {
    const created = (await apiCreateZone({
      unit: unit.id,
      name
    })) as import('./types/zone').ZoneResponse;
    invalidateAfterZoneCreate(unit.id);
    selectEntity('zone', created.id);
    showSuccess(`Zone "${created.name}" created`);
    return created;
  } catch (error) {
    showError(getErrorMessage(error));
    return null;
  }
}

export async function createRoom(zoneId: number) {
  const unitId = unitIdFromRoute();
  const name = await promptName('Room name');
  if (!name || !unitId) return null;

  try {
    const created = (await apiCreateRoom({
      zone: zoneId,
      name,
      description: ''
    })) as import('./types/room').RoomResponse;
    invalidateAfterRoomCreate(unitId);
    selectEntity('room', created.id);
    showSuccess(`Room "${created.name}" created`);
    return created;
  } catch (error) {
    showError(getErrorMessage(error));
    return null;
  }
}

export function createLoad(roomId: number) {
  document.dispatchEvent(new CustomEvent('open-create-load', { detail: { roomId } }));
}

export function createDevice(roomId: number) {
  document.dispatchEvent(new CustomEvent('open-create-device', { detail: { roomId } }));
}

export async function createRcu() {
  const unit = await requireUnit();
  if (!unit) return null;

  const allRcus = await fetchRCUs();
  const count = allRcus.filter((r) => r.unit === unit.id).length;
  try {
    const created = (await createRCU({
      name: `RCU ${count + 1}`,
      channel_count: 26
    })) as import('./types/rcu').RCUResponse;
    const linked = (await assignUnit(created.id, unit.id)) as import('./types/rcu').RCUResponse;
    invalidateAfterRcuUpdate(unit.id);
    selectEntity('rcu', linked.id);
    showSuccess(`"${linked.name}" created`);
    return linked;
  } catch (error) {
    showError(getErrorMessage(error));
    return null;
  }
}

export async function createScene() {
  const unit = await requireUnit();
  const name = await promptName('Scene name');
  if (!name || !unit) return null;

  try {
    const created = (await apiCreateScene({
      unit: unit.id,
      name,
      scope: 'unit',
      scope_id: unit.id,
      priority: 1
    })) as import('./types/scene').SceneResponse;
    invalidateAfterSceneCreate(unit.id);
    selectEntity('scene', created.id);
    showSuccess(`Scene "${created.name}" created`);
    return created;
  } catch (error) {
    showError(getErrorMessage(error));
    return null;
  }
}

/** @returns {number | null} */
export async function promptZoneForRoom() {
  const unitId = unitIdFromRoute();
  if (!unitId) return null;
  const zonesList = await fetchUnitZones(unitId);

  if (zonesList.length === 0) {
    showError('Create a zone first');
    return null;
  }
  if (zonesList.length === 1) return zonesList[0].id;

  const names = zonesList.map((z) => z.name).join(', ');
  const picked = window.prompt(`Zone name for new room (${names}):`);
  if (!picked?.trim()) return null;
  const zone = zonesList.find((z) => z.name.toLowerCase() === picked.trim().toLowerCase());
  if (!zone) {
    showError('Zone not found');
    return null;
  }
  return zone.id;
}

/** @returns {number | null} */
export async function promptRoomForChild(label: string) {
  const unitId = unitIdFromRoute();
  if (!unitId) return null;
  const roomsList = await fetchUnitRooms(unitId);
  if (roomsList.length === 0) {
    showError('Create a room first');
    return null;
  }
  if (roomsList.length === 1) return roomsList[0].id;

  const names = roomsList.map((r) => r.name).join(', ');
  const picked = window.prompt(`${label} — room name (${names}):`);
  if (!picked?.trim()) return null;
  const room = roomsList.find((r) => r.name.toLowerCase() === picked.trim().toLowerCase());
  if (!room) {
    showError('Room not found');
    return null;
  }
  return room.id;
}
